import React from 'react';
import { createPortal } from 'react-dom';
import XIcon from './icons/XIcon';
import CalendarIcon from './icons/CalendarIcon';
import MeatIcon from './icons/MeatIcon';
import type { Language } from '../types';
import type { RecipeUsageEntry } from '../utils/planUsageStats';
import { getTranslation, isRTL } from '../utils/translations';

interface RecipeUsageModalProps {
    isOpen: boolean;
    onClose: () => void;
    usageData: RecipeUsageEntry[];
    populatedDays: number;
    totalDays: number;
    meatWeight: number;
    titleKey: string;
    language?: Language;
}

const RecipeUsageModal: React.FC<RecipeUsageModalProps> = ({
    isOpen,
    onClose,
    usageData,
    populatedDays,
    totalDays,
    meatWeight,
    titleKey,
    language,
}) => {
    const t = (key: string) => getTranslation(language, key);
    const rtl = isRTL(language);

    if (!isOpen) return null;

    const totalRecipes = usageData.length;
    const totalServings = usageData.reduce((acc, curr) => acc + curr.totalServings, 0);
    const meatDisplay = meatWeight >= 1000
        ? `${(meatWeight / 1000).toFixed(1)} ${t('unit_kg')}`
        : `${Math.round(meatWeight)} ${t('unit_g')}`;

    return createPortal(
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                dir={rtl ? 'rtl' : 'ltr'}
                className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl w-full max-w-sm max-h-[80vh] flex flex-col overflow-hidden animate-scale-in border border-white/10 dark:border-slate-800"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="p-5 border-b bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 flex justify-between items-center gap-3">
                    <h3 className="font-black text-slate-800 dark:text-slate-100 text-lg tracking-tight">
                        {t(titleKey)}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 bg-slate-50 dark:bg-slate-800 rounded-full text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 shadow-sm transition-colors shrink-0"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 overflow-y-auto bg-white dark:bg-slate-900 scrollbar-none flex-grow">
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="flex flex-col items-center justify-center text-center p-3 rounded-2xl bg-sky-50/80 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-900/40">
                            <div className="bg-sky-50 dark:bg-sky-900/30 p-2 rounded-full text-sky-600 dark:text-sky-400 mb-2 shadow-sm">
                                <CalendarIcon className="w-5 h-5" />
                            </div>
                            <span className="text-lg font-black text-slate-800 dark:text-slate-200 tabular-nums">
                                {populatedDays}/{totalDays}
                            </span>
                            <span className="text-[9px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">
                                {t('unit_days')}
                            </span>
                        </div>

                        <div className="flex flex-col items-center justify-center text-center p-3 rounded-2xl bg-rose-50/80 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/40">
                            <div className="bg-rose-50 dark:bg-rose-900/30 p-2 rounded-full text-rose-500 dark:text-rose-400 mb-2 shadow-sm">
                                <MeatIcon className="w-5 h-5" />
                            </div>
                            <span className="text-lg font-black text-slate-800 dark:text-slate-200 tabular-nums">
                                {meatDisplay}
                            </span>
                            <span className="text-[9px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">
                                {t('meat')}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-row gap-3 mb-6">
                        <div className="flex-1 bg-orange-50 dark:bg-orange-900/10 rounded-2xl p-4 flex flex-col items-center justify-center border border-orange-100 dark:border-orange-800/30">
                            <span className="text-[10px] font-black uppercase tracking-widest text-orange-400 dark:text-orange-300 mb-1">
                                {t('recipes')}
                            </span>
                            <span className="text-3xl font-black text-orange-500 dark:text-orange-400 tabular-nums">
                                {totalRecipes}
                            </span>
                        </div>
                        <div className="flex-1 bg-blue-50 dark:bg-blue-900/10 rounded-2xl p-4 flex flex-col items-center justify-center border border-blue-100 dark:border-blue-800/30">
                            <span className="text-[10px] font-black uppercase tracking-widest text-blue-400 dark:text-blue-300 mb-1">
                                {t('totalLabel')} {t('servings')}
                            </span>
                            <span className="text-3xl font-black text-blue-500 dark:text-blue-400 tabular-nums">
                                {totalServings}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        {usageData.length > 0 ? (
                            usageData.map((item, index) => (
                                <div
                                    key={item.name}
                                    className="w-full bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800 py-4 flex flex-row items-center justify-between last:border-0 group hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors px-2 rounded-xl gap-3"
                                >
                                    <div className="flex items-center gap-4 min-w-0 flex-1">
                                        <span className="text-xs font-bold text-slate-300 dark:text-slate-600 group-hover:text-slate-400 dark:group-hover:text-slate-500 transition-colors shrink-0">
                                            #{index + 1}
                                        </span>
                                        <span className="text-sm font-bold text-slate-700 dark:text-slate-200 leading-tight line-clamp-2">
                                            {item.name}
                                        </span>
                                    </div>

                                    <div className="flex flex-row items-center gap-2 shrink-0">
                                        <div className="flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-800 rounded-xl w-12 h-12 border border-slate-100 dark:border-slate-700">
                                            <span className="text-sm font-black text-slate-800 dark:text-slate-100 tabular-nums">
                                                {item.count}
                                            </span>
                                            <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500 uppercase">
                                                {t('unit_times')}
                                            </span>
                                        </div>
                                        <div className="flex flex-col items-center justify-center bg-blue-50 dark:bg-blue-900/20 rounded-xl w-12 h-12 border border-blue-100 dark:border-blue-900/30">
                                            <span className="text-sm font-black text-blue-600 dark:text-blue-400 tabular-nums">
                                                {item.totalServings}
                                            </span>
                                            <span className="text-[8px] font-bold text-blue-400 dark:text-blue-300/70 uppercase">
                                                {t('servings')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-center py-12 text-slate-400 dark:text-slate-500 font-bold text-xs uppercase tracking-widest">
                                {t('noResults')}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default RecipeUsageModal;
